/**
 * event-edit.js
 */

import React from 'react';
import axios from 'axios';
import get from 'lodash.get';
import { useMediaQuery, useTheme, IconButton } from '@mui/material';
import { useHistory } from "react-router-dom";
import { useForm, useWatch, FormProvider } from 'react-hook-form';
import { none } from '@hookstate/core'
import { useSnackbar } from 'notistack';
import { Alert } from '@mui/material';
import { parseISO, parse, formatISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { TOUR_BEACON_VIEW_LIMIT, Types } from 'utils/constants';
import { dateParamFormat } from 'utils/datetime';
import { shouldRenderMobileLayout, renderForMobileContext } from 'utils/app-context';
import { makeLocation } from 'utils/routing';
import { sleep } from "utils/misc";
import useDebounce from 'utils/use-debounce';
import useMounted from 'utils/use-mounted';
import analytics from 'utils/analytics';
import { isFieldDirty } from 'utils/misc';
import { formatWeatherSummary } from 'utils/weather';
import useRecordStore from 'contexts/record-store';
import { UserContext } from 'contexts/user/user-context';
import { DogsContext } from 'contexts/dogs/dogs-context';
import { TourContext } from 'contexts/tour/tour-context';
import DesktopContentHeader from 'components/menu-drawer/desktop-content-header';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import EventTabs from '../container/event-tabs';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';
import DrawerContent from 'components/menu-drawer/drawer-content';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import TrainingWizardStepper from 'components/training/training-drawer/training-wizard-stepper';
import Icon from 'components/icon';
import LocationEdit from 'components/location-edit';
import useTours from 'components/tour/tour-data';
import TitleEditor from 'components/title-editor';
import FeatureBox from 'components/feature-box';
import EventEditDate from './event-edit-date';
import EventEditMembers from './event-edit-members';
import EventEditOptional from './event-edit-optional';
import { getSizeTypeAnalyticsLabel } from '../../training-utils';
import { TrainingGroupsProvider } from 'routing/routes/config/groups/training/training-groups-context';


// use wrapper to remount component when event ID changes
export default function EventEdit(props) {
  const eventId = props.params?.type === 'event' ? props.params?.id : null;

  return <EventEditImplementation 
    eventId={eventId}
    key={eventId} 
    {...props} 
  />;
}


function EventEditImplementation(props) {
  const {
    eventId,
    drawerState,
    onClose,
    wizard,
    basePath,
    params,
  } = props;
  const theme = useTheme();
  const translation = 'eventEdit';
  const { t } = useTranslation(translation);
  const isMounted = useMounted();
  const { user } = React.useContext(UserContext);
  const { dogs } = React.useContext(DogsContext);
  const { startTour } = useTours();
  const { activeTour, toursSeen, beaconViews } = React.useContext(TourContext);
  let history = useHistory();
  const path = history.location.pathname;
  const { enqueueSnackbar } = useSnackbar();
  const recordStore = useRecordStore();
  const action = params?.action || null;
  const eventRecord = recordStore.useRecord(Types.Record.EVENT, eventId);
  const [storedRecord, setStoredRecord] = React.useState();
  const [options, setOptions] = React.useState();
  const [groups, setGroups] = React.useState();
  const [submittedState, setSubmittedState] = React.useState();
  const [isNameCustomized, setIsNameCustomized] = React.useState(false);
  const [membersWithCompletions, setMembersWithCompletions] = React.useState();
  const [weatherSummary, setWeatherSummary] = React.useState(null);
  const [weatherLoading, setWeatherLoading] = React.useState(false);
  const weatherTimer = React.useRef();
  const weatherFetchDelay = React.useRef(0);
  const isSizeXS = useMediaQuery(theme.breakpoints.down('sm'));

  // hook form
  const formContext = useForm();
  const { control, formState, handleSubmit, reset } = formContext;
  const dirtyFields = formState.dirtyFields;
  const instantNameValue = useWatch({ control, name: 'name' });
  const nameValue = useDebounce(instantNameValue || '', 200);
  const dateValue = useWatch({ control, name: 'date' });
  const durationValue = useWatch({ control, name: 'duration' });
  const locationNameValue = useWatch({ control, name: 'location.locationName' });
  const address1Value = useWatch({ control, name: 'location.address1' });
  const cityValue = useWatch({ control, name: 'location.city' });
  const countryValue = useWatch({ control, name: 'location.country' });
  const regionValue = useWatch({ control, name: 'location.region' });
  const postCodeValue = useWatch({ control, name: 'location.postCode' });

  // props sent to sub-form components
  const formProps = {
    storedRecord,
    membersWithCompletions,
    options,
    drawerState,
    translation,
    isNameCustomized,
  };


  // check if we should start a product tour
  React.useEffect(() => {
    const isTourConditionsValid = activeTour < 0 && !isSizeXS && user !== null && dogs.length > 0;
    const isTourPathValid = path.includes("wz/1/create/event") || path.includes("/edit/event");
    const beaconViewCount = beaconViews[1] ?? 0;
    if (isTourConditionsValid && isTourPathValid && !toursSeen.includes(1) && beaconViewCount <= TOUR_BEACON_VIEW_LIMIT) {
      setTimeout(() => startTour(1), 1000); // Event editor tour
    }
  }, [path, isSizeXS, toursSeen, beaconViews, user, dogs, activeTour, startTour]);


  // get event options on init
  React.useEffect(() => {
    if (!Number.isInteger(eventId)) {
      return;
    }

    drawerState.loading.content.set(true);
    (async () => {
      try {
        const dateParam = new URLSearchParams(window.location.search).get('date');
        const date = formatISO(dateParam
          ? parse(dateParam, dateParamFormat, new Date())
          : new Date());
        const response = await axios.get(`/training/event/${eventId}/edit`, { params: { date } });

        if (!isMounted.current) return;
        if (response.status !== 200) return;

        // this gives processing time for the drawer to animate open before drawing the form
        await sleep(300);

        const editData = response.data;
        const trainingEvent = JSON.parse(JSON.stringify(recordStore.updateRecord(editData.event).get()));
        // Remove exercises from form data. With react-hook-form v7 these get stored and 
        // included with submission data even though there is no corresponding form field,
        // causing a submission error. Using shouldUnregister in useForm prevents this
        // but causes other problems.
        const { exercises: _, ...formInitData } = trainingEvent;
        reset(formInitData);
        setStoredRecord(trainingEvent);
        setMembersWithCompletions(trainingEvent.members.filter(m => m.hasCompletions));
        const { groups, ...optionsMinusGroups } = editData.options
        setOptions(optionsMinusGroups);
        setGroups(groups);

        drawerState.loading.content.set(false);
      } catch (e) {
        if (e.response && e.response.status === 404) {
          return history.push(basePath);
        }
        if (e.response && e.response.status === 403) {
          return history.push(makeLocation(
            basePath,
            'training',
            'viewEvent',
            {eventId},
            history.location
          ));
        }
      }
    })();

    // reset 0 state on closing so we don't have bad cached data later
    return () => {
      if (eventId === 0) {
        setTimeout(() => eventRecord.set(none), 1000);
      }
    }

  // recordStore & drawerState & eventRecord are hookstates
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, basePath, history, isMounted, reset]);


  const renderMembersWithCompletions = () => {
    return membersWithCompletions.map(m => m.id === user.id ? 'Me' : m.name).join(', ');
  }


  // resets event to changes
  const restoreOriginal = () => {
    if (storedRecord) {
      eventRecord.set(storedRecord);
    }
  }


  // when name is saved, update eventRecord
  React.useEffect(() => {
    if (nameValue) {
      eventRecord.set(p => ({
        ...p,
        name: nameValue
      }));
    }

    // if name is customized, prevent auto rename
    if (nameValue) {
      const autoNameRegex = /^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday) (Morning|Early Morning|Afternoon|Evening|Night) Training$/i;
      if (!autoNameRegex.test(nameValue)) {
        setIsNameCustomized(true);
      }
    }

    // eventRecord is setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameValue]);


  // when date, duration or location name changes, update eventRecord for menu consistency
  React.useEffect(() => {
    if (!dateValue || dateValue.toString() === 'Invalid Date'
      || durationValue === undefined
      || locationNameValue === undefined
    ) {
      return;
    }

    eventRecord.set(p => ({
      ...p,
      date: typeof dateValue === 'string'
        ? formatISO(parseISO(dateValue))
        : formatISO(dateValue),
      duration: durationValue,
      location: {
        ...p.location,
        locationName: locationNameValue
      }
    }));

    // eventRecord is setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateValue, durationValue, locationNameValue]);


  // when date or location details change, update weather
  // possible weatherSummary values:
  // '-'                : not enough info
  // 'null'             : not applicable, i.e. event is past, or before initializing
  // 'NOT AVAILABLE'    : error in last API call
  // formatted weather  : last API call successful
  React.useEffect(() => {
    clearTimeout(weatherTimer.current);

    const now = new Date();

    // Hide weather if event is in the past
    if (dateValue && new Date(dateValue) < now) {
      setWeatherSummary(null);
      return;
    }

    if (address1Value && cityValue && regionValue && countryValue && dateValue) {
      setWeatherLoading(true);

      // On all but the first load, wait 3 seconds before making the API call,
      // then start the timer again if weather data changes within that time.
      // This makes the experience smoother and reduces API calls when the user
      // is typing.
      weatherTimer.current = setTimeout(() => {
        (async () => {
          try {
            const response = await axios.get('/weather/address', {
              params: {
                address1: address1Value, 
                city: cityValue, 
                region: regionValue, 
                country: countryValue, 
                postCode: postCodeValue,
                datetime: dateValue,
              },
              bypassErrorHandling: true
            });
            if (!isMounted.current) return;
            if (response.status !== 200) {
              setWeatherSummary('NOT AVAILABLE')
            };
            const weather = response.data;
            setWeatherSummary(formatWeatherSummary(weather))
          } catch (e) {
            if (!isMounted.current) return;
            setWeatherSummary('NOT AVAILABLE')
          } finally {
            setWeatherLoading(false);
          }
        })();
      }, weatherFetchDelay.current);
      weatherFetchDelay.current = 3000;
    } else {
      setWeatherSummary('-');
    }

    return () => {
      clearTimeout(weatherTimer.current);
    };

  }, [dateValue, address1Value, cityValue, regionValue, countryValue, postCodeValue, isMounted]);


  // Ensure that ConfirmUnsavedEditor props are up to date by triggering 
  // a re-render before proceding with save and navigation.
  // (onSubmit > useEffect > afterSubmit)
  const onSubmit = React.useCallback((data, noNav) => {
    setSubmittedState({ data, noNav });
  }, []);


  const afterSubmit = React.useCallback(async (data, noNav) => {
    try {
      drawerState.loading.content.set(true);
      const isNewEvent = storedRecord.id === 0;
      const prevEventDate = isNewEvent ? null : storedRecord.date;
      const { record: savedEventRecord } = await recordStore.saveEvent(eventId, data, prevEventDate);

      if (savedEventRecord?.get()) {
        if (wizard) {
          if (isNewEvent) {
            // track which predicted fields were changed
            let dirtyPredictedFields = [];
            if (dirtyFields.name) dirtyPredictedFields.push('Name');
            if (dirtyFields.date) dirtyPredictedFields.push('DateTime');
            if (dirtyFields.duration) dirtyPredictedFields.push('Duration');
            if (isFieldDirty(get(formState.dirtyFields, 'location'))) dirtyPredictedFields.push('Location');
            if (dirtyFields.groupId || dirtyFields.members) dirtyPredictedFields.push('Members');
            const fields = dirtyPredictedFields.length > 0 ? dirtyPredictedFields.join(',') : 'None';
            analytics.trackGA4('event', { name: 'event_predictionfix', params: { modified_fields: fields } });

            // track mandatory/optional usage
            const creatorId = savedEventRecord.get().creator.id;
            let allMandatory = true;
            let allOptional = true;
            savedEventRecord.get().members
              .filter(m => m.id !== creatorId)
              .forEach(m => {
                if (m.isOptional) {
                  allMandatory = false;
                } else {
                  allOptional = false;
                }
              });
            const memberCount = savedEventRecord.get().members.length;
            const attendType = memberCount === 1
              ? 'one_member'
              : allMandatory
                ? 'all_mandatory'
                : allOptional
                  ? 'all_optional'
                  : 'mandatory_and_optional';
            analytics.trackGA4('event', { 
              name: 'event_attendance', 
              params: { 
                attendance_type: attendType, 
                member_count: memberCount,
                group_size_type: getSizeTypeAnalyticsLabel(memberCount)
              } 
            });
          }

          if (noNav !== true) {
            // wizard event save
            wizard.onSave(savedEventRecord.get(), history, basePath);
          }
        } else {
          // regular event save
          enqueueSnackbar('Event saved', { variant: 'success' });
          if (noNav !== true) {
            history.push(makeLocation(
              basePath,
              'training',
              'viewEvent',
              {eventId},
              history.location
            ));
          }
        }
      }
    } catch (e) {
      enqueueSnackbar('Error saving event', {
        variant: 'error',
        persist: true,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });
      throw e;
    } finally {
      drawerState.loading.content.set(false);
    }

  // recordStore & drawerState & exerciseRecord are hookstates
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, basePath, enqueueSnackbar, history, wizard, dirtyFields, isMounted, storedRecord]);


  React.useEffect(() => {
    if (submittedState) {
      afterSubmit(submittedState.data, submittedState.noNav);
    }
    // only call afterSubmit when submittedState changes, not when afterSubmit itself changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedState]);


  const onSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);


  const onCancel = React.useCallback(() => {
    // go back to event view
    history.push(makeLocation(
      basePath,
      'training',
      'viewEvent',
      {eventId},
      history.location
    ));
  }, [history, basePath, eventId]);


  const saveButton = React.useMemo(() => (
    <DrawerActionButton
      level={1}
      disabled={!formState.isDirty && !wizard}
      style={{ marginLeft: 16 }}
      onClick={onSave}
      data-testid='event-save-button'
    >
      {wizard ? 'Next' : 'Save'}
    </DrawerActionButton>
  ), [wizard, onSave, formState.isDirty]);


  const cancelButton = React.useMemo(() => (
    <DrawerActionButton
      onClick={onCancel}
    >
      Cancel
    </DrawerActionButton>
  ), [onCancel]);


  const closeButton = React.useMemo(() => (
    <IconButton
      data-testid="mobile-drawer-close-button"
      size="small"
      style={{color: 'inherit'}}
      onClick={onClose}
    >
      <Icon type='close' />
    </IconButton>
  ), [onClose]);


  const desktopTitle = React.useMemo(() => (
    <>
      {storedRecord &&
      <TitleEditor
        propertyName='name'
        inputLabel={t('optional.name.label')}
        helperText={!isNameCustomized ? t('optional.name.extraInfo') : ''}
        editButtonTestId='edit-event-name-button'
        saveButtonTestId='save-event-name-button'
        validationRules={{
          required: t('optional.name.errMsgEmpty'),
          validate: value => value.trim() !== '' || t('optional.name.errMsgEmpty'),
          maxLength: {
            value: 150,
            message: t('optional.name.errMsgLength')
          }
        }}
      />}
    </>
  ), [storedRecord, isNameCustomized, t]);


  const stickyHeader = (
    <>
      {/* mobile & wizard -> show new/existing tabs */}
      {(shouldRenderMobileLayout() && wizard) && 
      <EventTabs value='new' basePath={basePath} />}

      {/* desktop */}
      {!shouldRenderMobileLayout() &&
      <>
        {/* wizard -> show event tabs + actions */}
        {wizard && 
        <DesktopContentHeader 
          title={<EventTabs value='new' basePath={basePath} />} 
          actions={saveButton} 
        />}

        {/* no wizard -> show regular title + actions */}
        {!wizard && 
        <DesktopContentHeader
          title={nameValue}
          actions={<>{cancelButton}{saveButton}</>}
        />}
      </>}
    </>
  );


  const weatherPreview = (
    <>
      {weatherSummary !== null &&
        <FeatureBox loading={weatherLoading}>
          <span style={{ fontWeight: 500 }}>Forecast: </span>
          {weatherSummary}
        </FeatureBox>
      }
    </>
  );


  return (
    <>
      <FormProvider {...formContext}>
        <DrawerContentBuilder
          mobileAppBar={{ 
            title: 'Event', 
            leftActions: eventId === 0 ? closeButton : cancelButton,
            rightActions: saveButton,
            recordGroupType: Types.RecordGroup.TRAINING,
          }}
          desktopHeader={{ 
            title: desktopTitle,
            actions: <>
              {!wizard && cancelButton}
              {saveButton}
            </>
          }}
          stickyHeader={{ content: stickyHeader, offset: -20 }}
          children={(triggerElement, desktopHeader) => (storedRecord && options && groups) ? (
            <TrainingGroupsProvider groups={groups}>    
              <form onSubmit={handleSubmit(onSubmit)} data-testid='event-editor-form'>
                {desktopHeader}
                <MobilePageTitle title={eventRecord.get()?.name} />
                <DrawerContent>

                  {shouldRenderMobileLayout() && wizard &&
                  <TrainingWizardStepper basePath={basePath} />}

                  {triggerElement}

                  {wizard && action !== 'edit' &&
                  <EventTabs value='new' basePath={basePath} style={{ marginBottom: 32 }} />}

                  {/* has completions warning */}
                  {membersWithCompletions.length > 0 &&
                  <Alert severity="warning" style={{ marginBottom: 24 }}>
                    <>
                      One or more exercises within this event have already been completed. Members who have completed exercises cannot be removed from the event. Members With Completions: {renderMembersWithCompletions()}.
                    </>
                  </Alert>}

                  {/* event time & duration */}
                  <EventEditDate {...formProps} />

                  {/* location & address */}
                  <LocationEdit
                    locationOptions={options.locations}
                    countryOptions={options.countries}
                    drawerState={drawerState}
                    recordType={Types.Record.EVENT}
                    doAutofill={renderForMobileContext() && storedRecord.id === 0}
                    weatherPreview={weatherPreview}
                  />

                  {/* groups & members */}
                  <EventEditMembers {...formProps} />

                  {/* additional options */}
                  <EventEditOptional {...formProps} />

                </DrawerContent>

              </form>
            </TrainingGroupsProvider>          

            ) : null}
          />      
        </FormProvider>
  

      <ConfirmUnsavedEditor
        dirty={formState.isDirty && !formState.isSubmitted}
        discard={restoreOriginal}
        save={async () => {
          return await new Promise((resolve) => {
            handleSubmit(
              async (data) => {
                onSubmit(data, true);
                return resolve(true);
              },
              () => {
                return resolve(false);
              })();
          });
        }}
      />
    </>
  );
}